/**
 * Workshop ConversationEmbed — inline frame embed inside a conversation thread.
 *
 * Production wiring (copied patterns, never imported — workshop must not
 * depend on production shape code):
 * - Today assistant messages are static markdown only
 *   (conversation-frame-thread.tsx:346-364); related frames live in the
 *   header ConversationArtifactMenu dropdown and open via
 *   presentArtifactBesideConversation. Clicks navigate via denker://frame/<id>
 *   links (conversation-frame-links.ts, focus-frame-activation.ts).
 * - This workshop component simulates that flow: the expand button calls
 *   `onExpand?.(frameId)` (the workshop stand-in for
 *   presentArtifactBesideConversation) and toggles the full frame inline.
 * - Bubble recipe copied from conversation-frame-thread.tsx:355 — user:
 *   `max-w-[84%] whitespace-pre-wrap rounded-preview bg-accent/18 px-3 py-2
 *   text-canvas-base leading-relaxed text-primary` (right); assistant: bare
 *   `max-w-[92%] text-canvas-base text-secondary` (left). The embed itself
 *   sits inside an assistant turn.
 *
 * Native-source note: agenda/event rows are the existing workshop
 * AgendaFrame/EventFrame (spec-pinned recreations — no official OSS
 * component exists); board rows copy the kanban recipe; draft/attachment use
 * PreviewCardFrame with imageUrl="" so the offline icon fallback renders and
 * no network fetch happens. Denker glass outside via surfaceRoleClassName.
 * No FrameHeader/FrameFooter — hover pill only.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { AgendaFrame } from "@/workshop/components/shapes/agenda-frame/agenda-frame";
import { BoardFrame } from "@/workshop/components/shapes/board-frame/board-frame";
import { EventFrame } from "@/workshop/components/shapes/event-frame/event-frame";
import { PreviewCardFrame } from "@/workshop/components/shapes/preview-card-frame/preview-card-frame";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

type ConversationEmbedKind =
  | "agenda"
  | "event"
  | "draft"
  | "attachment"
  | "board";

interface ConversationEmbedProps {
  frameId: string;
  frameKind: ConversationEmbedKind;
  title: string;
  subtitle?: string;
  preview?: string;
  status?: FrameStatus;
  defaultExpanded?: boolean;
  onExpand?: (frameId: string) => void;
  className?: string;
  "data-testid"?: string;
}

// ─── Mock data (top of file) ─────────────────────────────────────────────────

const KIND_META: Record<
  ConversationEmbedKind,
  { label: string; Icon: typeof Icons.Calendar }
> = {
  agenda: { label: "Agenda", Icon: Icons.Calendar },
  event: { label: "Event", Icon: Icons.CalendarClock },
  draft: { label: "Draft", Icon: Icons.Edit },
  attachment: { label: "Attachment", Icon: Icons.FileText },
  board: { label: "Board", Icon: Icons.Kanban },
};

const COMPACT_AGENDA_ITEMS = [
  {
    id: "c1",
    time: "14:30",
    title: "Keynote — Designing with AI",
    detail: "Config, Berlin · Main stage",
    current: true,
  },
  {
    id: "c2",
    time: "16:00",
    title: "Design systems crit",
    detail: "Studio call · 45 min",
  },
];

const FULL_AGENDA_ITEMS = [
  {
    id: "c0",
    time: "12:00",
    title: "Lunch with Priya",
    detail: "Kreuzberg · 1h",
  },
  ...COMPACT_AGENDA_ITEMS,
  {
    id: "c3",
    time: "18:30",
    title: "Dinner with Config crew",
    detail: "Markthalle Neun · 2h",
  },
];

const COMPACT_BOARD_COLUMNS = [
  {
    id: "todo",
    label: "To do",
    dotColor: "bg-warning",
    cards: [{ id: "t1", title: "Confirm keynote seats", meta: "P1 · today" }],
  },
  {
    id: "doing",
    label: "Doing",
    dotColor: "bg-status-selected",
    cards: [{ id: "d1", title: "Draft Felix recap", meta: "Denker" }],
  },
];

const FULL_BOARD_COLUMNS = [
  ...COMPACT_BOARD_COLUMNS,
  {
    id: "done",
    label: "Done",
    dotColor: "bg-accent",
    cards: [{ id: "f1", title: "Book flights", meta: "done" }],
  },
];

// ─── Main component ──────────────────────────────────────────────────────────

export function ConversationEmbed({
  frameId,
  frameKind,
  title,
  subtitle,
  preview,
  status = "done",
  defaultExpanded = false,
  onExpand,
  className,
  "data-testid": testId = "conversation-embed",
}: ConversationEmbedProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const meta = KIND_META[frameKind];
  const KindIcon = meta.Icon;

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(true);
    onExpand?.(frameId);
  };

  const handleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(false);
  };

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex w-full flex-col overflow-hidden rounded-frame",
        className,
      )}
      data-testid={`${testId}-${frameId}`}
      {...surfaceRoleAttributes("frame")}
    >
      <div
        className="pointer-events-none absolute left-2 right-2 top-2 z-10 flex justify-center opacity-0 transition-opacity group-hover:opacity-100"
        data-testid={`${testId}-${frameId}-hover-title`}
      >
        <span className="truncate rounded-pill bg-glass-fill-dense px-2.5 py-0.5 text-canvas-xs text-secondary">
          {title || meta.label}
        </span>
      </div>

      <div
        className="nodrag flex flex-1 flex-col gap-2 px-3 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-2"
          data-testid={`${testId}-${frameId}-header`}
        >
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-control bg-elevated/60"
            aria-hidden="true"
          >
            <KindIcon className="h-3.5 w-3.5 text-secondary" />
          </span>
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-canvas-sm font-semibold text-primary"
              data-testid={`${testId}-${frameId}-title`}
            >
              {title || `Untitled ${meta.label.toLowerCase()}`}
            </p>
            {subtitle && (
              <p
                className="truncate text-canvas-xs text-muted"
                data-testid={`${testId}-${frameId}-subtitle`}
              >
                {subtitle}
              </p>
            )}
          </div>
          {status === "streaming" && (
            <span
              className="shrink-0 rounded-pill bg-accent/18 px-2 py-0.5 text-canvas-xs font-medium text-accent"
              data-testid={`${testId}-${frameId}-status`}
            >
              Live
            </span>
          )}
          {status === "error" && (
            <span
              className="shrink-0 rounded-pill bg-danger-muted px-2 py-0.5 text-canvas-xs font-medium text-status-error"
              data-testid={`${testId}-${frameId}-status`}
            >
              Failed
            </span>
          )}
          {expanded ? (
            <button
              type="button"
              onClick={handleCollapse}
              data-testid={`${testId}-${frameId}-collapse-button`}
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-pill px-2.5 py-1",
                "bg-white/[0.06] text-canvas-xs font-medium text-secondary",
                "transition-colors hover:bg-white/[0.1] hover:text-primary",
              )}
            >
              <Icons.ChevronUp className="h-3 w-3" aria-hidden="true" />
              Collapse
            </button>
          ) : (
            <button
              type="button"
              onClick={handleExpand}
              data-testid={`${testId}-${frameId}-expand-button`}
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-pill px-2.5 py-1",
                "bg-white/[0.06] text-canvas-xs font-medium text-secondary",
                "transition-colors hover:bg-white/[0.1] hover:text-primary",
              )}
            >
              <Icons.ChevronDown className="h-3 w-3" aria-hidden="true" />
              Expand
            </button>
          )}
        </div>

        {!title ? (
          <p
            className="py-3 text-center text-canvas-sm italic text-muted"
            data-testid={`${testId}-${frameId}-empty`}
          >
            No {meta.label.toLowerCase()} to show
          </p>
        ) : (
          <div data-testid={`${testId}-${frameId}-preview`}>
            {frameKind === "agenda" && (
              <AgendaFrame
                date={subtitle ?? "Wednesday, Oct 22"}
                statusPill={expanded ? "1 conflict resolved" : undefined}
                items={expanded ? FULL_AGENDA_ITEMS : COMPACT_AGENDA_ITEMS}
                status={status}
                className="w-full"
                data-testid={`${testId}-${frameId}-agenda`}
              />
            )}
            {frameKind === "event" && (
              <EventFrame
                title={title}
                detail={subtitle ?? "Wednesday · 14:30 – 14:50 · Config, Berlin"}
                status={status}
                className="w-full"
                data-testid={`${testId}-${frameId}-event`}
              />
            )}
            {frameKind === "board" && (
              <BoardFrame
                title={title}
                path={subtitle}
                columns={expanded ? FULL_BOARD_COLUMNS : COMPACT_BOARD_COLUMNS}
                status={status}
                className="w-full"
                data-testid={`${testId}-${frameId}-board`}
              />
            )}
            {(frameKind === "draft" || frameKind === "attachment") && (
              <PreviewCardFrame
                imageUrl=""
                title={title}
                source={subtitle ?? preview ?? meta.label}
                openLabel={frameKind === "draft" ? "Open draft" : "Open file"}
                status={status}
                className="w-full"
                data-testid={`${testId}-${frameId}-preview-card`}
              />
            )}
          </div>
        )}

        {preview && title && !expanded && (
          <p
            className="truncate text-canvas-xs text-muted"
            data-testid={`${testId}-${frameId}-snippet`}
          >
            {preview}
          </p>
        )}
      </div>
    </div>
  );
}

export type { ConversationEmbedProps, ConversationEmbedKind };
